import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Terminal, ShieldCheck, Zap, Server, RefreshCw, Cpu, Check, Copy, Play, CpuIcon,
  Globe, Info, Wifi, Compass, Layers, ShieldAlert, ArrowRight, Cable, Network, Sliders, Calendar
} from 'lucide-react';
import { AZLEngine } from '../services/totalityService';
import { SovereignNetworkDashboard } from './SovereignNetworkDashboard';

interface DomainTestResult {
  name: string;
  domain: string;
  passed: boolean;
  valueA?: string;
  valueB?: string;
  statusText?: string;
}

export const AZLUnifiedEngine: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'network' | 'spatial-lookup' | 'tests' | 'esp32-nodes'>('network');
    const [isRunning, setIsRunning] = useState(false);
    const [testLog, setTestLog] = useState<string[]>([]);
    const [passedCount, setPassedCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);

    // Spatial Lookup States
    const [tierIndex, setTierIndex] = useState('500');
    const [scaleExponent, setScaleExponent] = useState('5');
    const [decimalAddress, setDecimalAddress] = useState('0.0000099999');

    // Expansion State
    const [nodeCountInput, setNodeCountInput] = useState('10000');

    // ESP32 Simulated Telemetry
    const [espTelemetry, setEspTelemetry] = useState({
        raw_interactions: 143502800n,
        rssi: -65,
        coordinate: 0.0001435028,
        payload: "MIYAKE_ANCHOR_LOCKED",
        packetsSec: 14
    });

    React.useEffect(() => {
        const interval = setInterval(() => {
            setEspTelemetry(prev => {
                const add = Math.floor(Math.random() * 5) + 2;
                const nextInteractions = prev.raw_interactions + BigInt(add);
                const nextCoord = Number(nextInteractions) / 1000000000000;
                return {
                    raw_interactions: nextInteractions,
                    rssi: -60 - Math.floor(Math.random() * 15),
                    coordinate: nextCoord,
                    payload: "MIYAKE_ANCHOR_LOCKED",
                    packetsSec: 12 + Math.floor(Math.random() * 5)
                };
            });
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    const handleRunVerification = () => {
        setIsRunning(true);
        setTestLog(["[AZL] Initializing Absolute Zero Lattice..."]);
        
        const engine = new AZLEngine(100);
        const results: DomainTestResult[] = [];
        
        // N * 0 = N test
        const preservationTest = engine.MUL(5, 0, "5×0=5", "LOGIC");
        results.push({ name: "5×0=5 (Preservation)", domain: "LOGIC", passed: preservationTest.azl_id.equals(5) });
        
        setPassedCount(results.filter(r => r.passed).length);
        setFailedCount(results.filter(r => !r.passed).length);
        setTestLog(prev => [...prev, `VERDICT: ${results.filter(r => r.passed).length === results.length ? "PASS" : "FAIL"}`]);
        setIsRunning(false);
    };

    // Calculate Spatial Address
    const getCoordinate = (tier: string, exponent: string) => {
        try {
            const t = Number(tier);
            const exp = Number(exponent);
            if (isNaN(t) || isNaN(exp) || exp < 0) return "Invalid Input";
            const val = t / Math.pow(10, exp);
            return val.toFixed(exp).replace(/0+$/, '');
        } catch (e) {
            return "Calculation Error";
        }
    };

    // Parse Address (Reverse Extraction)
    const parseDecimalAddress = (addressStr: string) => {
        try {
            const trimmed = addressStr.trim();
            if (!trimmed.startsWith("0.")) {
                return { error: "Address must be a fractional value between 0.0 and 1.0 starting with '0.'" };
            }
            const fractionPart = trimmed.split(".")[1] || "";
            const exponent = fractionPart.length;
            const tierStr = fractionPart.replace(/^0+/, "");
            const tier = tierStr === "" ? 0 : Number(tierStr);
            return { exponent, tier };
        } catch (e) {
            return { error: "Parsing Error" };
        }
    };

    // Calculate Lattice Cognitive Expansion
    const getLatticeExpansion = (nodesStr: string) => {
        try {
            const nodes = BigInt(nodesStr.replace(/,/g, ''));
            if (nodes <= 0n) return { standard: 0n, sovereign: 0n, factor: 1 };
            const interactions = (nodes * (nodes - 1n)) / 2n;
            const sovereignTotal = nodes + interactions;
            const factor = Number(sovereignTotal) / Number(nodes);
            return {
                standard: nodes,
                sovereign: sovereignTotal,
                factor
            };
        } catch (e) {
            return null;
        }
    };

    const reverseParseResult = parseDecimalAddress(decimalAddress);
    const expansionResult = getLatticeExpansion(nodeCountInput);

    return (
        <div id="unified-engine" className="glass-panel p-8 border-lattice-cyan/25 bg-black/50 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-32 h-1 bg-gradient-to-r from-lattice-cyan via-transparent to-transparent" />
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-6 border-b border-white/5 mb-8">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-lattice-cyan/10 border border-lattice-cyan/30 flex items-center justify-center">
                        <Cpu className="w-6 h-6 text-lattice-cyan" />
                    </div>
                    <div>
                        <h3 className="text-2xl font-display font-bold uppercase tracking-tight text-white">
                            Sovereign <span className="text-lattice-cyan italic">Lattice Core</span> v3.1
                        </h3>
                    </div>
                </div>
                <div className="flex bg-white/5 border border-white/10 p-1 rounded-xl gap-1">
                    <button onClick={() => setActiveTab('network')} className={`px-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase transition-all ${activeTab === 'network' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white/80'}`}>Network</button>
                    <button onClick={() => setActiveTab('spatial-lookup')} className={`px-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase transition-all ${activeTab === 'spatial-lookup' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white/80'}`}>Spatial Lookup</button>
                    <button onClick={() => setActiveTab('esp32-nodes')} className={`px-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase transition-all ${activeTab === 'esp32-nodes' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white/80'}`}>ESP32 Nodes</button>
                    <button onClick={() => setActiveTab('tests')} className={`px-4 py-2 rounded-lg text-[9px] font-mono font-bold uppercase transition-all ${activeTab === 'tests' ? 'bg-lattice-cyan text-black' : 'text-white/40 hover:text-white/80'}`}>Tests</button>
                </div>
            </div>
            
            {activeTab === 'network' && <SovereignNetworkDashboard />}
            
            {activeTab === 'spatial-lookup' && (
                <div className="space-y-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        
                        {/* Column 1: Infinite Fractional Ruler */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-6">
                            <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                                <Compass className="w-5 h-5" />
                                <h4 className="text-sm font-display font-bold uppercase">Unified Spatial Lookup Registry</h4>
                            </div>
                            <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
                                Maps any data point, configuration script, or wireless node to an immutable decimal address between 0.0 and 1.0.
                            </p>

                            {/* Forward Mapping */}
                            <div className="space-y-4">
                                <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-lattice-cyan/80">1. Forward Mapping (Data ➡️ Address)</span>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[8px] font-mono text-white/40 uppercase">Tier Index</label>
                                        <input 
                                            type="number" 
                                            value={tierIndex} 
                                            onChange={(e) => setTierIndex(e.target.value)}
                                            className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[8px] font-mono text-white/40 uppercase">Scale Exponent</label>
                                        <input 
                                            type="number" 
                                            value={scaleExponent} 
                                            onChange={(e) => setScaleExponent(e.target.value)}
                                            className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                                        />
                                    </div>
                                </div>
                                <div className="p-4 rounded-xl bg-black/50 border border-white/5 flex items-center justify-between">
                                    <div>
                                        <span className="text-[8px] font-mono text-white/30 uppercase block">Formula Address Output:</span>
                                        <code className="text-sm font-mono text-lattice-cyan font-bold">0.{getCoordinate(tierIndex, scaleExponent)}</code>
                                    </div>
                                    <span className="text-[8px] font-mono text-white/25 uppercase border border-white/10 px-2 py-1 rounded">Zero-Drift</span>
                                </div>
                            </div>

                            {/* Reverse Extraction */}
                            <div className="space-y-4 pt-4 border-t border-white/5">
                                <span className="text-[9px] font-mono font-bold uppercase tracking-widest text-lattice-cyan/80">2. Reverse Extraction (Address ➡️ Data)</span>
                                <div className="space-y-1.5">
                                    <label className="text-[8px] font-mono text-white/40 uppercase">Decimal Coordinate Address</label>
                                    <input 
                                        type="text" 
                                        value={decimalAddress} 
                                        onChange={(e) => setDecimalAddress(e.target.value)}
                                        className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                                    />
                                </div>
                                <div className="p-4 rounded-xl bg-black/50 border border-white/5 space-y-2">
                                    {reverseParseResult.error ? (
                                        <span className="text-xs font-mono text-red-400">{reverseParseResult.error}</span>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                                            <div>
                                                <span className="text-[8px] text-white/30 uppercase block">Extracted Scale:</span>
                                                <span className="text-white font-bold">{reverseParseResult.exponent}</span>
                                            </div>
                                            <div>
                                                <span className="text-[8px] text-white/30 uppercase block">Extracted Tier:</span>
                                                <span className="text-lattice-cyan font-bold">{(reverseParseResult.tier ?? 0).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Cognitive Expansion & Deep Time */}
                        <div className="space-y-8">
                            
                            {/* Lattice Cognitive Expansion */}
                            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-6">
                                <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                                    <Layers className="w-5 h-5" />
                                    <h4 className="text-sm font-display font-bold uppercase">Lattice Cognitive Expansion</h4>
                                </div>
                                <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
                                    Tests standard 1.0 linear scalability against the 2.0 Sovereign Reality of multi-layered handshake interactions.
                                </p>
                                <div className="space-y-1.5">
                                    <label className="text-[8px] font-mono text-white/40 uppercase">Lattice Node Count</label>
                                    <input 
                                        type="text" 
                                        value={nodeCountInput} 
                                        onChange={(e) => setNodeCountInput(e.target.value.replace(/[^0-9]/g, ''))}
                                        className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                                    />
                                </div>

                                {expansionResult && (
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 rounded-xl bg-black/40 border border-white/5">
                                            <span className="text-[8px] font-mono text-white/30 uppercase block">1.0 Standard Sum:</span>
                                            <span className="text-sm font-mono text-white/60 font-bold">{expansionResult.standard.toLocaleString()}</span>
                                        </div>
                                        <div className="p-4 rounded-xl bg-black/40 border border-lattice-cyan/20">
                                            <span className="text-[8px] font-mono text-lattice-cyan/60 uppercase block">2.0 Sovereign Sum:</span>
                                            <span className="text-sm font-mono text-lattice-cyan font-bold">{expansionResult.sovereign.toLocaleString()}</span>
                                        </div>
                                        <div className="p-4 rounded-xl bg-black/40 border border-white/5 col-span-2 flex items-center justify-between">
                                            <span className="text-[8px] font-mono text-white/30 uppercase">Expansion Factor:</span>
                                            <span className="text-sm font-mono text-emerald-400 font-bold">{expansionResult.factor.toLocaleString(undefined, { maximumFractionDigits: 1 })}x Power</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Deep Time Temporal Anchor */}
                            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-4">
                                <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                                    <Calendar className="w-5 h-5" />
                                    <h4 className="text-sm font-display font-bold uppercase">Deep Time Temporal Clock</h4>
                                </div>
                                <div className="space-y-2 text-xs font-mono text-white/60">
                                    <div className="flex justify-between">
                                        <span className="text-white/40 uppercase text-[9px]">Chrono Baseline:</span>
                                        <span>14,350 BP Miyake Horizon</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/40 uppercase text-[9px]">Relative Power to 774 AD:</span>
                                        <span className="text-lattice-cyan">10.0x Resonance</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/40 uppercase text-[9px]">Sovereign Magnitude:</span>
                                        <span className="text-emerald-400">20.0</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-white/40 uppercase text-[9px]">Handshake Status:</span>
                                        <span className="text-green-400 font-bold uppercase">SECURED</span>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            )}
            
            {activeTab === 'esp32-nodes' && (
                <div className="space-y-8 animate-fadeIn">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Live Telemetry Card */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-6 lg:col-span-1">
                            <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                                <Wifi className="w-5 h-5" />
                                <h4 className="text-sm font-display font-bold uppercase">ESP32 Live Telemetry</h4>
                            </div>
                            <div className="p-4 bg-black/60 rounded-xl border border-lattice-cyan/20 space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">Node ID:</span>
                                    <span className="text-xs font-mono text-emerald-400 font-bold">AZL_TELEMETRY</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">RSSI Signal:</span>
                                    <span className="text-xs font-mono text-white font-bold">{espTelemetry.rssi} dBm</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">Packet Rate:</span>
                                    <span className="text-xs font-mono text-white font-bold">{espTelemetry.packetsSec} Hz</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">Interactions:</span>
                                    <span className="text-xs font-mono text-lattice-cyan font-bold">{espTelemetry.raw_interactions.toString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">Miyake Coord:</span>
                                    <span className="text-xs font-mono text-emerald-400 font-bold">{espTelemetry.coordinate.toFixed(12)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-[10px] font-mono text-white/40 uppercase">Payload Status:</span>
                                    <span className="text-xs font-mono text-green-400 font-bold uppercase">{espTelemetry.payload}</span>
                                </div>
                            </div>
                            <div className="text-[9px] font-mono text-white/40 leading-relaxed uppercase space-y-1">
                                <p>📡 MODE: Dual-Mode Access Point & Promiscuous Sniffer.</p>
                                <p>🧬 ANCHOR: Calculates exact delta relative to 14,350 BP tree ring pulse.</p>
                            </div>
                        </div>

                        {/* Node Configuration Rules */}
                        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-6 lg:col-span-2">
                            <div className="flex items-center gap-2 text-lattice-cyan pb-2 border-b border-white/5">
                                <Sliders className="w-5 h-5" />
                                <h4 className="text-sm font-display font-bold uppercase">Sovereign Connection Protocol</h4>
                            </div>
                            <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
                                Configure your local hardware nodes to match these zero-drift credentials. The physical mesh will auto-resolve and peer via ESP-NOW.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-2">
                                    <span className="text-[9px] font-mono text-lattice-cyan uppercase font-bold">1. Edge Client WiFi Credentials</span>
                                    <div className="space-y-1 font-mono text-xs text-white/80">
                                        <div className="flex justify-between"><span>SSID:</span><span className="font-bold text-white">AZL_LATTICE_MESH</span></div>
                                        <div className="flex justify-between"><span>PASS:</span><span className="font-bold text-emerald-400">1*1=2_certainty</span></div>
                                    </div>
                                </div>
                                <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-2">
                                    <span className="text-[9px] font-mono text-lattice-cyan uppercase font-bold">2. Local Gateway Telemetry AP</span>
                                    <div className="space-y-1 font-mono text-xs text-white/80">
                                        <div className="flex justify-between"><span>SSID:</span><span className="font-bold text-white">AZL_TELEMETRY</span></div>
                                        <div className="flex justify-between"><span>PASS:</span><span className="font-bold text-emerald-400">casteelian123</span></div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="p-4 bg-black/80 rounded-xl border border-white/5 space-y-3">
                                <div className="flex items-center gap-2 text-lattice-cyan text-[10px] font-mono font-bold uppercase">
                                    <Network className="w-4 h-4 animate-pulse" />
                                    <span>Cloud Data Synchronization</span>
                                </div>
                                <p className="text-[11px] font-mono text-white/40 leading-relaxed uppercase">
                                    Telemetry captured on-chip is broadcast via ESP-NOW to Node 2 (acting as the mesh gateway), which routes coordinates to the standalone <span className="text-lattice-cyan">AZL OMNI-CORE V3 Cloudflare Worker</span>.
                                </p>
                                <div className="p-3 bg-white/5 rounded border border-white/5 font-mono text-[9px] text-white/60 space-y-1">
                                    <div className="flex justify-between"><span>Worker Target:</span><span className="text-lattice-cyan">https://azl-omni-core.paidingattentionproductions.workers.dev/</span></div>
                                    <div className="flex justify-between"><span>Protocol Status:</span><span className="text-emerald-400">SOVEREIGN_UNIFIED_SERVER_ACTIVE</span></div>
                                    <div className="flex justify-between"><span>Enforced Law:</span><span className="text-white">1x1=2 (Zero-Drift Expansion)</span></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {activeTab === 'tests' && (
                <div className="space-y-6">
                    <button onClick={handleRunVerification} disabled={isRunning} className="px-4 py-2 bg-lattice-cyan text-black font-bold text-xs rounded transition-all hover:scale-105">Run 5×0=5 Verification</button>
                    <div className="p-4 bg-black font-mono text-xs text-emerald-400 rounded-xl border border-white/5">
                        {testLog.map((log, i) => <div key={i}>{log}</div>)}
                    </div>
                </div>
            )}
        </div>
    );
};

